<template>
  <div class="hr-dashboard">
    <template v-if="selectedOption">

      <!-- ── Breadcrumb strip ───────────────────────────────── -->
      <div class="hr-dashboard__header">
        <div class="hr-dashboard__header-left">
          <v-btn
            v-if="showBackButton"
            variant="text"
            color="primary"
            size="small"
            class="hr-dashboard__back-btn"
            @click="backToEmployeeList"
          >
            <v-icon start size="15">mdi-arrow-left</v-icon>
            {{ t("app.actions.back") || t("hrDashboard.back") }}
          </v-btn>
          <v-icon v-if="currentOption" class="hr-dashboard__header-icon" size="16">
            {{ currentOption.icon }}
          </v-icon>
          <h1 v-if="currentOption" class="hr-dashboard__title">{{ currentOption.label }}</h1>
          <span v-if="currentOption?.subtitle" class="hr-dashboard__subtitle">
            {{ currentOption.subtitle }}
          </span>
        </div>
        <div v-if="isAdmin && companyOptions.length" class="hr-dashboard__header-right">
          <v-select
            v-model="companySelectModel"
            :items="companyOptions"
            item-title="label"
            item-value="id"
            hide-details
            :loading="companiesLoading"
            :disabled="companiesLoading"
            :label="t('hrDashboard.activeCompanyLabel')"
            density="compact"
            class="hr-dashboard__company-select"
          />
        </div>
        <HrCompanyName v-else-if="!isAdmin && selectedCompanyName" :name="selectedCompanyName" />
      </div>

      <!-- ── Content ────────────────────────────────────────── -->
      <v-alert v-if="companiesError" type="warning">
        {{ companiesError }}
      </v-alert>
      <v-alert
        v-else-if="requiresCompanySelection && !selectedCompanyId"
        type="info"
      >
        Selectează compania activă pentru a continua în modul HR.
      </v-alert>
      <component
        v-else
        :is="currentComponent"
        :key="currentComponentKey"
        v-bind="currentComponentProps"
        v-on="componentListeners"
      />

    </template>
  </div>
</template>

<script>
import { ref, computed, nextTick, onMounted, onBeforeUnmount, watch } from "vue";
import axios from "axios";
import { useI18n } from "vue-i18n";
import Companies from "@/components/Companies.vue";
import HrCompanyName from "@/components/dashboard/HrCompanyName.vue";
import CompanyDetails from "@/components/CompanyDetails.vue";
import Employees from "@/components/Employees.vue";
import ZileLibere from "@/components/holiday/ZileLibere.vue";
import EmployeeDetails from "@/components/EmployeeDetails.vue";
import EmployeeFiles from "@/components/employee/EmployeeFiles.vue";
import EmployeeTimesheet from "@/components/EmployeeTimesheet.vue";
import UserAccountSettings from "@/components/UserAccountSettings.vue";
import ChecklistPanel from "@/components/checklist/ChecklistPanel.vue";
import { useRouter, useRoute } from "vue-router";
import { useCompanyContext } from "@/Utile/companyContext.js";
import EmployeeFinance from "@/components/payroll/EmployeeFinance.vue";
import { stopSmartTrackerOnLogout } from "@/Utile/smartTrackerRuntime.mjs";
import { clearAuthStorage } from "@/Utile/authStorage.mjs";
import { parseCompanyId, resolveCompanyDisplayName } from "@/components/dashboard/utils.mjs";
import { buildHrDashboardMenuOptions } from "@/Utile/hrDashboardMenuOptions.mjs";

export default {
  name: "HrDashboard",
  components: {
    Companies,
    HrCompanyName,
    CompanyDetails,
    Employees,
    ZileLibere,
    EmployeeDetails,
    EmployeeFiles,
    EmployeeTimesheet,
    UserAccountSettings,
    ChecklistPanel,
    EmployeeFinance,
  },
  setup() {
    const { t, locale } = useI18n();
    const router = useRouter();
    const route = useRoute();
    const HR_SELECTED_EMPLOYEE_KEY = "hr-dashboard:selected-employee-id";
    const HR_SELECTED_EMPLOYEE_COMPANY_KEY = "hr-dashboard:selected-employee-company-id";
    const userRole = ref((localStorage.getItem("user-role") || "").toLowerCase());
    const userRolesList = (() => {
      try {
        const stored = JSON.parse(localStorage.getItem("user-roles") || "[]");
        return Array.isArray(stored) ? stored.map((r) => String(r.key || r.role || r || "").toLowerCase()) : [];
      } catch (_) {
        return [];
      }
    })();
    const isAdmin = computed(() => userRole.value === "admin" || userRolesList.includes("admin"));
    const userCompanyId = ref(
      (() => {
        const raw = localStorage.getItem("user-company-id");
        const numeric = raw !== null && raw !== undefined ? Number(raw) : null;
        return Number.isNaN(numeric) ? null : numeric;
      })()
    );
    const userEmployeeId = ref(
      (() => {
        const raw = localStorage.getItem("user-employee-id");
        const numeric = raw !== null && raw !== undefined ? Number(raw) : null;
        return Number.isNaN(numeric) ? null : numeric;
      })()
    );
    const { companyId: contextCompanyId, companyName: contextCompanyName, setCompanyContext } =
      useCompanyContext();
    const availableCompanies = ref([]);
    const companiesLoading = ref(false);
    const companiesError = ref("");
    const activeCompanyName = ref(null);

    const parseEmployeeId = (value) => {
      const numeric = Number(value);
      return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
    };

    const isHr = computed(() => userRole.value === "hr");
    const timesheetRoleSet = computed(() => new Set([userRole.value, ...userRolesList].filter(Boolean)));
    const timesheetCanManage = computed(() =>
      ["admin", "miniadmin", "hr", "vacation-hr"].some((roleKey) => timesheetRoleSet.value.has(roleKey))
    );
    const selectedCompanyId = computed(() => parseCompanyId(activeCompanyId.value) || parseCompanyId(contextCompanyId.value) || parseCompanyId(userCompanyId.value) || null);
    const selectedCompanyName = computed(() => {
      const id = selectedCompanyId.value;
      if (!id) return null;
      const match = availableCompanies.value.find((c) => Number(c.id) === Number(id));
      if (match) return resolveCompanyDisplayName(match) || String(match.id);
      return contextCompanyName.value || null;
    });

    const getStoredEmployeeId = () => parseEmployeeId(localStorage.getItem(HR_SELECTED_EMPLOYEE_KEY));
    const setStoredEmployeeId = (id) => {
      const parsed = parseEmployeeId(id);
      if (!parsed) return;
      localStorage.setItem(HR_SELECTED_EMPLOYEE_KEY, String(parsed));
    };
    const getStoredEmployeeCompanyId = () =>
      parseCompanyId(localStorage.getItem(HR_SELECTED_EMPLOYEE_COMPANY_KEY));
    const setStoredEmployeeCompanyId = (companyId) => {
      const parsed = parseCompanyId(companyId);
      if (!parsed) {
        localStorage.removeItem(HR_SELECTED_EMPLOYEE_COMPANY_KEY);
        return;
      }
      localStorage.setItem(HR_SELECTED_EMPLOYEE_COMPANY_KEY, String(parsed));
    };
    const clearStoredEmployeeId = () => {
      localStorage.removeItem(HR_SELECTED_EMPLOYEE_KEY);
      localStorage.removeItem(HR_SELECTED_EMPLOYEE_COMPANY_KEY);
    };

    const isEmployeeDetailRoute = computed(() => route.name === "HrDashboardEmployeeDetails");
    const DETAIL_OPTION_IDS = [
      "employee-management",
      "my-data",
      "my-files",
      "my-timesheet",
      "finance",
      "checklist",
    ];
    const HOME_QUICK_OPTION_IDS = new Set([
      "company-management",
      "company-board",
      "employee-management",
      "checklist",
      "my-data",
      "my-timesheet",
      "annual-leave",
      "my-files",
      "finance",
      "user-account",
    ]);

    const buildMenuOptions = () => {
      const currentLocale = locale.value; // ensure reactive updates on locale change
      void currentLocale;
      return buildHrDashboardMenuOptions({ t, isAdmin: isAdmin.value });
    };

    const menuOptions = computed(() => {
      const ALL_MENU_OPTIONS = buildMenuOptions();
      const baseOptions = isHr.value
        ? ALL_MENU_OPTIONS.filter((option) => option.id !== "annual-leave")
        : ALL_MENU_OPTIONS;

      if (isEmployeeDetailRoute.value) {
        return baseOptions
          .filter(
            (option) =>
              !["company-management", "employee-management", "annual-leave", "user-account"].includes(
                option.id
              )
          );
      }

      return baseOptions.filter((option) => !["my-files", "my-data"].includes(option.id));
    });

    const selectedOption = ref(null);
    const previousOption = ref(null);
    const lastActiveCompanyId = ref(null);
    const activeCompanyId = ref(null);
    const companyViewMode = ref("overview");
    const employeeDetailId = ref(null);
    watch(
      menuOptions,
      (options) => {
        if (!options.length) return;
        if (!selectedOption.value || !options.some((opt) => opt.id === selectedOption.value)) {
          selectedOption.value = options[0].id;
        }
      },
      { immediate: true }
    );
    onMounted(() => {
      fetchCompanies();
      if (contextCompanyId.value) {
        setActiveCompany(contextCompanyId.value);
      }
    });
    const currentOption = computed(() => {
      if (!selectedOption.value) return null;
      const allOptions = buildMenuOptions();
      return (
        menuOptions.value.find((option) => option.id === selectedOption.value) ||
        allOptions.find((option) => option.id === selectedOption.value) ||
        null
      );
    });

    const currentComponent = computed(() => {
      if (selectedOption.value === "company-management" && activeCompanyId.value) {
        if (companyViewMode.value === "employees") {
          return "Employees";
        }
        if (companyViewMode.value === "employee-details" && employeeDetailId.value) {
          return "EmployeeDetails";
        }
        return "CompanyDetails";
      }
      if (selectedOption.value === "company-board") {
        return "CompanyDetails";
      }
      if (selectedOption.value === "employee-management") {
        if (employeeDetailId.value) {
          return "EmployeeDetails";
        }
        return "Employees";
      }
      if (selectedOption.value === "my-data") {
        return "EmployeeDetails";
      }
      if (selectedOption.value === "my-timesheet") {
        return "EmployeeTimesheet";
      }
      if (selectedOption.value === "my-files") {
        return "EmployeeFiles";
      }
      if (selectedOption.value === "user-account") {
        return "UserAccountSettings";
      }
      return currentOption.value?.component || null;
    });

    const currentComponentProps = computed(() => {
      const effectiveCompanyId = selectedCompanyId.value || lastActiveCompanyId.value || null;

      if (selectedOption.value === "company-management" && effectiveCompanyId) {
        if (companyViewMode.value === "employees") {
          return {
            companyIdFilter: effectiveCompanyId,
            showCompanySelect: false,
            openInDashboard: true,
            useHrDetailRoute: true,
          };
        }
        if (companyViewMode.value === "employee-details" && employeeDetailId.value) {
          return {
            employeeId: employeeDetailId.value,
            inlineNavigation: true,
            companyId: effectiveCompanyId,
          };
        }
        return {
          companyId: effectiveCompanyId,
          allowCompanyEdit: true,
        };
      }
      if (selectedOption.value === "company-board") {
        return {
          companyId: effectiveCompanyId,
        };
      }
      if (selectedOption.value === "employee-management") {
        if (employeeDetailId.value) {
          return {
            employeeId: employeeDetailId.value,
            inlineNavigation: true,
            companyId: effectiveCompanyId,
          };
        }
        return {
          ...(effectiveCompanyId
            ? {
                companyIdFilter: effectiveCompanyId,
                showCompanySelect: false,
              }
            : {}),
          useHrDetailRoute: true,
        };
      }
      if (selectedOption.value === "my-data") {
        const id = employeeDetailId.value || getStoredEmployeeId() || parseEmployeeId(userEmployeeId.value);
        return {
          employeeId: id,
          inlineNavigation: true,
          companyId: effectiveCompanyId,
        };
      }
      if (selectedOption.value === "my-files") {
        const id = employeeDetailId.value || getStoredEmployeeId() || parseEmployeeId(userEmployeeId.value);
        return {
          employeeId: id,
        };
      }
      if (selectedOption.value === "finance") {
        const id = employeeDetailId.value || getStoredEmployeeId() || parseEmployeeId(userEmployeeId.value);
        return {
          employeeId: id,
          companyId: selectedCompanyId.value || lastActiveCompanyId.value || null,
          canAddBonus: true,
          allowCompanyFallback: false,
        };
      }
      if (selectedOption.value === "my-timesheet") {
        const selfId =
          employeeDetailId.value ||
          parseEmployeeId(userEmployeeId.value) ||
          getStoredEmployeeId();
        return {
          employeeId: selfId,
          canManage: timesheetCanManage.value,
          title: t("hrDashboard.menuItems.myTimesheet.label"),
        };
      }
      if (selectedOption.value === "checklist") {
        const id = employeeDetailId.value || getStoredEmployeeId() || null;
        return {
          mode: "hr",
          companyId: effectiveCompanyId,
          ...(id ? { employeeId: id } : {}),
        };
      }
      if (selectedOption.value === "user-account") {
        return {};
      }
      return {};
    });

    const currentComponentKey = computed(() => {
      if (selectedOption.value === "company-management" && activeCompanyId.value) {
        const suffix = employeeDetailId.value ? `-${employeeDetailId.value}` : "";
        return `${companyViewMode.value}-${activeCompanyId.value}${suffix}`;
      }
      if (selectedOption.value === "company-board") {
        return `company-board-${activeCompanyId.value || "none"}`;
      }
      if (selectedOption.value === "employee-management") {
        return employeeDetailId.value
          ? `employee-detail-${employeeDetailId.value}`
          : selectedOption.value;
      }
      if (selectedOption.value === "my-data") {
        return employeeDetailId.value ? `my-data-${employeeDetailId.value}` : "my-data";
      }
      if (selectedOption.value === "my-files") {
        return employeeDetailId.value ? `my-files-${employeeDetailId.value}` : "my-files";
      }
      if (selectedOption.value === "my-timesheet") {
        return "my-timesheet";
      }
      if (selectedOption.value === "finance") {
        return `finance-${employeeDetailId.value || getStoredEmployeeId() || parseEmployeeId(userEmployeeId.value) || "self"}`;
      }
      return selectedOption.value || "none";
    });

    const componentListeners = computed(() => {
      if (
        ["company-management", "company-board"].includes(selectedOption.value) ||
        currentComponent.value === "CompanyDetails" ||
        currentComponent.value === "Companies"
      ) {
        return {
          "open-company-details": openCompanyDetails,
          "open-employees": openCompanyEmployees,
          "open-checklist": openCompanyChecklist,
          "company-updated": handleCompanyCatalogChange,
          "companies-changed": handleCompanyCatalogChange,
          back: handleEmployeesBack,
        };
      }
      return {};
    });

    const companyOptions = computed(() =>
      availableCompanies.value.map((c) => {
        const id = Number(c.id);
        const name = resolveCompanyDisplayName(c) || `Companie ${id}`;
        return {
          id,
          name,
          label: name,
        };
      })
    );

    const setActiveCompany = (id) => {
      if (!id) {
        activeCompanyId.value = null;
        activeCompanyName.value = null;
        setCompanyContext({ id: null });
        return;
      }
      const numericId = Number(id);
      const match = companyOptions.value.find((c) => Number(c.id) === numericId);
      const name = match?.name || selectedCompanyName.value || null;
      activeCompanyId.value = numericId;
      activeCompanyName.value = name;
      lastActiveCompanyId.value = numericId;
      setCompanyContext({ id: numericId, name });
    };

    const handleCompanyChange = (newId) => {
      setActiveCompany(newId);
      if (selectedOption.value === "company-management") {
        companyViewMode.value = "overview";
        employeeDetailId.value = null;
      }
    };

    const fetchCompanies = async () => {
      companiesLoading.value = true;
      companiesError.value = "";
      try {
        const { data } = await axios.get("/candidati/companies");
        availableCompanies.value = Array.isArray(data) ? data : [];
        if (!selectedCompanyId.value) {
          if (contextCompanyId.value) {
            setActiveCompany(contextCompanyId.value);
          } else if (availableCompanies.value.length === 1) {
            setActiveCompany(availableCompanies.value[0].id);
          } else if (userCompanyId.value) {
            setActiveCompany(userCompanyId.value);
          }
        }
      } catch (err) {
        companiesError.value = "Nu am putut încărca lista companiilor.";
        console.error(err);
      } finally {
        companiesLoading.value = false;
      }
    };

    const companySelectModel = computed({
      get: () => selectedCompanyId.value,
      set: (val) => handleCompanyChange(val),
    });

    const requiresCompanySelection = computed(() =>
      ["company-board", "employee-management", "checklist"].includes(selectedOption.value)
    );

    const selectOption = async (optionId) => {
      if (optionId === "logout") {
        await stopSmartTrackerOnLogout();
        clearAuthStorage();
        router.push("/login");
        return;
      }

      if (optionId !== selectedOption.value && optionId !== "logout") {
        previousOption.value = selectedOption.value;
      }
      selectedOption.value = optionId;

      if (!DETAIL_OPTION_IDS.includes(optionId)) {
        employeeDetailId.value = null;
        clearStoredEmployeeId();
      } else if (isEmployeeDetailRoute.value && !employeeDetailId.value) {
        const stored = getStoredEmployeeId();
        if (stored) {
          employeeDetailId.value = stored;
        }
      }
      if (optionId === "company-management") {
        if (isHr.value && userCompanyId.value) {
          activeCompanyId.value = userCompanyId.value;
          lastActiveCompanyId.value = userCompanyId.value;
          companyViewMode.value = "overview";
        }
      } else if (optionId === "company-board") {
        const targetCompany = activeCompanyId.value || lastActiveCompanyId.value || userCompanyId.value;
        if (targetCompany) {
          activeCompanyId.value = targetCompany;
          lastActiveCompanyId.value = targetCompany;
        }
        companyViewMode.value = "overview";
      } else {
        activeCompanyId.value = null;
        companyViewMode.value = "overview";
      }
      await nextTick();
    };

    const handleOptionClick = async (optionId) => {
      if (optionId === "company-management" && selectedOption.value === "company-management") {
        activeCompanyId.value = null;
        companyViewMode.value = "overview";
      }

      if (optionId === "my-data") {
        const selfId = parseEmployeeId(userEmployeeId.value);
        if (selfId) {
          setStoredEmployeeId(selfId);
          employeeDetailId.value = selfId;
        }
        if (!isEmployeeDetailRoute.value) {
          router.push({ name: "HrDashboardEmployeeDetails" });
        }
      }

      if (optionId === "my-files") {
        const storedId = getStoredEmployeeId() || parseEmployeeId(userEmployeeId.value);
        if (storedId) {
          setStoredEmployeeId(storedId);
          employeeDetailId.value = storedId;
        }
        if (!isEmployeeDetailRoute.value) {
          router.push({ name: "HrDashboardEmployeeDetails" });
        }
      }

      if (optionId === "my-timesheet") {
        const selfId =
          parseEmployeeId(userEmployeeId.value) ||
          employeeDetailId.value ||
          getStoredEmployeeId();
        if (selfId) {
          setStoredEmployeeId(selfId);
          employeeDetailId.value = selfId;
        }
      }

      if (isEmployeeDetailRoute.value && !DETAIL_OPTION_IDS.includes(optionId)) {
        router.push({ name: "HrDashboard" });
      }
      await selectOption(optionId);
    };

    const resolveHomeQuickOption = () => {
      const raw = Array.isArray(route.query.option) ? route.query.option[0] : route.query.option;
      return String(raw || "").toLowerCase();
    };

    const applyHomeQuickOptionFromRoute = () => {
      if (!route.path.startsWith("/hr-dashboard")) return;
      const requestedOption = resolveHomeQuickOption();
      if (!HOME_QUICK_OPTION_IDS.has(requestedOption)) return;
      // Use the full unfiltered options (not the route-context-filtered subset)
      // so options like "my-data" / "my-files" — which are hidden from the main
      // route view but valid navigation targets — are still handled correctly.
      if (!buildMenuOptions().some((option) => option.id === requestedOption)) return;
      if (selectedOption.value === requestedOption) return;
      handleOptionClick(requestedOption);
    };

    const openCompanyDetails = (companyId) => {
      if (!companyId) return;
      setActiveCompany(companyId);
      companyViewMode.value = "overview";
      employeeDetailId.value = null;
      if (selectedOption.value === "company-board") {
        previousOption.value = "company-board";
        selectedOption.value = "company-management";
      }
    };

    const openCompanyEmployees = (companyId) => {
      if (!companyId) return;
      setActiveCompany(companyId);
      companyViewMode.value = "employees";
      employeeDetailId.value = null;
      if (selectedOption.value !== "company-management") {
        previousOption.value = selectedOption.value;
        selectedOption.value = "company-management";
      }
    };

    const openCompanyChecklist = (companyId) => {
      if (!companyId) return;
      setActiveCompany(companyId);
      employeeDetailId.value = null;
      selectedOption.value = "checklist";
    };

    const handleCompanyCatalogChange = async (payload = {}) => {
      const deletedCompanyId = Number(payload.deletedCompanyId) || null;
      const targetCompanyId =
        Number(payload.companyId || activeCompanyId.value || lastActiveCompanyId.value) || null;
      await fetchCompanies();
      if (deletedCompanyId && deletedCompanyId === Number(activeCompanyId.value)) {
        activeCompanyId.value = null;
        companyViewMode.value = "overview";
        return;
      }
      if (targetCompanyId) {
        setActiveCompany(targetCompanyId);
      }
    };

    const resetCompanyView = () => {
      activeCompanyId.value = null;
      companyViewMode.value = "overview";
      employeeDetailId.value = null;
    };

    const handleEmployeesBack = () => {
      const target = previousOption.value || "company-management";
      selectedOption.value = target;

      if (target === "company-management") {
        if (lastActiveCompanyId.value) {
          activeCompanyId.value = lastActiveCompanyId.value;
        }
        companyViewMode.value = "overview";
      } else {
        activeCompanyId.value = null;
        companyViewMode.value = "overview";
      }

      employeeDetailId.value = null;
    };

    const openEmployeeDetailsInline = (employeeId, companyId = null) => {
      const parsedId = parseEmployeeId(employeeId);
      if (!parsedId) return;
      setStoredEmployeeId(parsedId);
      setStoredEmployeeCompanyId(companyId);
      if (selectedOption.value === "company-management" && activeCompanyId.value) {
        employeeDetailId.value = parsedId;
        companyViewMode.value = "employee-details";
        return;
      }
      // „Datele mele” (profilul propriu) deschide un subordonat în același workspace de detalii, ca „Angajați”.
      if (selectedOption.value === "my-data") selectedOption.value = "employee-management";
      if (selectedOption.value === "employee-management") {
        employeeDetailId.value = parsedId;
        if (isEmployeeDetailRoute.value) {
          setStoredEmployeeId(parsedId);
        }
      }
    };

    const handleOpenEmployeeEvent = (event) => {
      const id = event?.detail?.employeeId;
      if (!id) return;
      openEmployeeDetailsInline(id, event?.detail?.companyId);
    };

    const backToEmployeeList = () => {
      if (selectedOption.value === "company-management" && companyViewMode.value === "employee-details") {
        companyViewMode.value = "employees";
        employeeDetailId.value = null;
        return;
      }

      if (DETAIL_OPTION_IDS.includes(selectedOption.value) && employeeDetailId.value) {
        employeeDetailId.value = null;
        if (isEmployeeDetailRoute.value) {
          clearStoredEmployeeId();
          router.push({ name: "HrDashboard" });
        }
      }
      if (isEmployeeDetailRoute.value && DETAIL_OPTION_IDS.includes(selectedOption.value)) {
        selectedOption.value = "employee-management";
      }
    };

    const showBackButton = computed(() => {
      if (selectedOption.value === "company-management") {
        return companyViewMode.value === "employee-details";
      }
      if (DETAIL_OPTION_IDS.includes(selectedOption.value)) {
        return Boolean(employeeDetailId.value);
      }
      return false;
    });

    const syncEmployeeDetailRouteState = () => {
      if (!isEmployeeDetailRoute.value) return;
      const storedId = getStoredEmployeeId();
      if (!storedId) {
        clearStoredEmployeeId();
        router.replace({ name: "HrDashboard" });
        return;
      }
      const storedCompanyId = getStoredEmployeeCompanyId();
      if (storedCompanyId) {
        lastActiveCompanyId.value = storedCompanyId;
        if (!selectedCompanyId.value) {
          setCompanyContext({ id: storedCompanyId });
        }
      }

      if (!DETAIL_OPTION_IDS.includes(selectedOption.value)) {
        selectedOption.value =
          storedId === parseEmployeeId(userEmployeeId.value) ? "my-data" : "employee-management";
      }
      employeeDetailId.value = storedId;
      activeCompanyId.value = null;
      companyViewMode.value = "overview";
    };

    watch(
      () => route.fullPath,
      () => {
        if (!isEmployeeDetailRoute.value && DETAIL_OPTION_IDS.includes(selectedOption.value)) {
          employeeDetailId.value = null;
          if (selectedOption.value === "my-files") {
            selectedOption.value = "employee-management";
          }
        }
        syncEmployeeDetailRouteState();
        applyHomeQuickOptionFromRoute();
      },
      { immediate: true }
    );

    watch(
      () => employeeDetailId.value,
      (newId) => {
        if (!isEmployeeDetailRoute.value) return;
        if (newId) {
          setStoredEmployeeId(newId);
        } else {
          clearStoredEmployeeId();
        }
      }
    );

    watch(
      () => contextCompanyId.value,
      (newId) => {
        if (newId) {
          setActiveCompany(newId);
        }
      }
    );

    onMounted(() => {
      window.addEventListener("ext-dashboard:open-subordinate", handleOpenEmployeeEvent);
    });

    onBeforeUnmount(() => {
      window.removeEventListener("ext-dashboard:open-subordinate", handleOpenEmployeeEvent);
    });

    return {
      menuOptions,
      selectedOption,
      previousOption,
      currentOption,
      currentComponent,
      currentComponentProps,
      currentComponentKey,
      activeCompanyId,
      companyViewMode,
      selectOption,
      handleOptionClick,
      openCompanyDetails,
      openCompanyEmployees,
      openCompanyChecklist,
      handleCompanyCatalogChange,
      resetCompanyView,
      handleEmployeesBack,
      backToEmployeeList,
      showBackButton,
      t,
      companyOptions,
      companySelectModel,
      companiesLoading,
      companiesError,
      requiresCompanySelection,
      selectedCompanyId,
      selectedCompanyName,
      isAdmin,
      componentListeners,
    };
  },
};
</script>

<style src="./HrDashboard.styles.css" />
